import { fetchCurrentUser, setCurrentUser } from '@/features/users';
import { authApi } from '@/shared/api/auth';
import { getAccessToken, setAccessToken } from '@/shared/api/index';
import { useAppDispatch } from '@/shared/hooks';
import type { CurrentUser } from '@/shared/types/user';
import { parseJson } from '@/shared/utils/json';
import type { AppDispatch } from '@/store';
import { useEffect, useState } from 'react';

let globalAuthState: boolean | null = null;
const authListeners: Set<(isAuthenticated: boolean | null) => void> = new Set();
let authCheckInProgress = false;

const notifyListeners = (isAuthenticated: boolean | null) => {
  globalAuthState = isAuthenticated;
  authListeners.forEach((listener) => listener(isAuthenticated));
};

const checkAuthOnce = async (dispatch?: AppDispatch) => {
  if (authCheckInProgress) return;
  authCheckInProgress = true;

  const existingToken = getAccessToken();

  if (existingToken) {
    // Restore user from localStorage if available
    const savedUser = localStorage.getItem('currentUser');
    if (savedUser && dispatch) {
      try {
        const user = parseJson<CurrentUser>(savedUser);
        dispatch(setCurrentUser(user));
      } catch {
        localStorage.removeItem('currentUser');
      }
    }
    notifyListeners(true);
  } else {
    try {
      const response = await authApi.refresh();
      setAccessToken(response.accessToken);
      notifyListeners(true);
    } catch {
      setAccessToken(null);
      localStorage.removeItem('currentUser');
      notifyListeners(false);
    }
  }

  authCheckInProgress = false;
};

export const triggerAuthCheck = () => {
  const token = getAccessToken();
  notifyListeners(!!token);
};

export const useAuth = () => {
  const dispatch = useAppDispatch();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(globalAuthState);

  useEffect(() => {
    const listener = (newAuthState: boolean | null) => {
      setIsAuthenticated(newAuthState);

      // Load user data when authenticated
      if (newAuthState === true) {
        void dispatch(fetchCurrentUser());
      }
    };

    authListeners.add(listener);

    if (globalAuthState === null && !authCheckInProgress) {
      void checkAuthOnce(dispatch);
    }

    return () => {
      authListeners.delete(listener);
    };
  }, [dispatch]);

  return { isAuthenticated };
};
